import type { AuthProvider } from 'react-admin';
import { getSession, signOutCurrentUser } from './cognito';
import { parseIdentity, parseRole, type IdTokenPayload } from './token';

const idTokenPayload = async (): Promise<IdTokenPayload> => {
  const session = await getSession();
  return session.getIdToken().decodePayload() as IdTokenPayload;
};

/**
 * React Admin auth provider backed by Cognito.
 *
 * The interactive multi-step login (forced password change, TOTP enrolment,
 * TOTP challenge) is driven by the custom LoginPage talking to the Cognito SDK
 * directly. By the time `login` is called the SDK has already authenticated and
 * cached the tokens, so `login` simply confirms a valid session exists; React
 * Admin then performs the post-login redirect (honouring `?next=`).
 */
export const authProvider: AuthProvider = {
  // Confirm the LoginPage's challenge flow left us with a valid session.
  login: async () => {
    await getSession();
  },

  logout: async () => {
    // signOutCurrentUser needs config; ignore if the pool was never set up.
    try {
      signOutCurrentUser();
    } catch {
      // not configured / no user — nothing to sign out
    }
  },

  checkAuth: async () => {
    await getSession();
  },

  checkError: async (error: { status?: number }) => {
    if (error?.status === 401 || error?.status === 403) {
      throw new Error('Unauthorized');
    }
  },

  getIdentity: async () => parseIdentity(await idTokenPayload()),

  getPermissions: async () => parseRole(await idTokenPayload()),
};
